import { memo, ReactNode } from 'react';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import Box from '@amzn/awsui-components-core/box';

import {
  FeedbackComments,
  RumFeedbackViewModel,
  RumFeedbackViewModelProps,
  useRumFeedbackViewModel,
} from './RumFeedbackViewModel';

/**
 * Props for the base RumFeedback component.
 *
 * Accepts a `children` render prop that receives the view model, allowing
 * each variant (thumbs, stars, etc.) to render its own controls and
 * feedback modal while reusing the shared state/RUM logic.
 */
export interface RumFeedbackProps<
  TValue extends string,
  TFeedback extends FeedbackComments = string,
> extends RumFeedbackViewModelProps<TValue, TFeedback> {
  /** Label shown before the rating control. */
  readonly label: string;
  /** Render prop that receives the view model and renders the rating control and modal. */
  readonly children: (vm: RumFeedbackViewModel<TValue, TFeedback>) => ReactNode;
}

function RumFeedbackInner<TValue extends string, TFeedback extends FeedbackComments = string>({
  label,
  children,
  ...viewModelProps
}: RumFeedbackProps<TValue, TFeedback>) {
  const vm = useRumFeedbackViewModel(viewModelProps);

  return (
    <>
      <SpaceBetween direction="horizontal" size="xs" alignItems="center">
        <Box variant="span" fontWeight="bold">
          {label}
        </Box>
        {children(vm)}
      </SpaceBetween>
    </>
  );
}

/**
 * Base RUM feedback component.
 *
 * Manages rating state and emits CloudWatch RUM events via the `composeEvent`
 * callback. Visual variants (e.g. ThumbsFeedback) wrap this component and
 * supply their own controls and feedback modal via the `children` render prop.
 */
const RumFeedback = memo(RumFeedbackInner) as typeof RumFeedbackInner;

export default RumFeedback;
